import React from 'react';
import { L } from '../../theme/luxe';
import {
  ZH_MONTHS, EN_MONTHS, STAT_KEYS, reportMonth, nextMonthOf, statVal, planLines, fmtMD, toDate,
} from './sopShared';

// 月度服务报告 as the member sees it (Luxe, editorial). One renderer for the
// member page, the coach's live preview and the supervisor's 「按会员看到的样子
// 预览」, so the three can never drift apart.
//
// report: { period_index, period_start, period_end, published_at, opening,
//           stats: { contacts_two_way, recommended, interested, meetings },
//           timeline: [{ date, text }], learned, plan,
//           author: { first_name, role_label }, reviewer: { first_name } }
// compact: the small card used beside the coach's editor.
// footer: extra nodes rendered under the team block (往期报告 on the member page).

const rule = `1px solid ${L.line}`;
const kick = (extra) => ({ fontFamily: L.sans, fontSize: 11, letterSpacing: '.24em', color: L.mute, ...extra });

export default function ReportPaper({ report, isZh = true, compact = false, footer = null, contactAction = null }) {
  const r = report || {};
  const m = reportMonth(r);
  const mi = m ? m.getMonth() : null;
  const year = m ? m.getFullYear() : '';
  const stats = r.stats || {};
  const timeline = (Array.isArray(r.timeline) ? r.timeline : []).filter((x) => x && x.text && x.include !== false);
  const plan = planLines(r.plan);
  const nextMi = nextMonthOf(r);
  const authorName = (r.author && (r.author.first_name || r.author.name)) || '';
  const roleLabel = (r.author && r.author.role_label) || (isZh ? '主服务老师' : 'Lead consultant');
  const reviewerName = (r.reviewer && (r.reviewer.first_name || r.reviewer.name)) || '';
  const published = toDate(r.published_at);
  const kickerLine = [
    r.period_index ? (isZh ? `第 ${r.period_index} 期` : `No. ${r.period_index}`) : '',
    mi != null ? (isZh ? `${year} 年 ${mi + 1} 月` : `${EN_MONTHS[mi]} ${year}`) : '',
  ].filter(Boolean).join(' · ');

  if (compact) {
    return (
      <div style={{ background: L.paper, border: rule, borderRadius: 14, padding: '18px 16px', display: 'flex', flexDirection: 'column', gap: 10, color: L.ink, fontFamily: L.sans }}>
        <span style={kick({ fontSize: 10, letterSpacing: '.22em' })}>{kickerLine}</span>
        {mi != null && (
          <div style={{ fontFamily: L.serif, fontSize: 34, lineHeight: 1 }}>
            {isZh ? ZH_MONTHS[mi] : EN_MONTHS[mi]}{' '}
            {isZh && <span style={{ fontStyle: 'italic', color: L.goldDeep, fontSize: 26 }}>{EN_MONTHS[mi]}</span>}
          </div>
        )}
        {r.opening && <p style={{ margin: 0, fontFamily: "'Noto Serif SC', serif", fontSize: 12.5, lineHeight: 1.7, color: L.ink2, whiteSpace: 'pre-wrap' }}>{r.opening}</p>}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', borderTop: `1px solid ${L.ink}`, paddingTop: 8, textAlign: 'center' }}>
          {STAT_KEYS.map(([k, , , short, en]) => (
            <div key={k}>
              <div style={{ fontFamily: L.serif, fontSize: 24 }}>{statVal(stats[k])}</div>
              <div style={{ fontSize: 9.5, color: L.ink2 }}>{isZh ? short : en}</div>
            </div>
          ))}
        </div>
        {timeline.length > 0 && (
          <>
            <div style={kick({ fontSize: 10, letterSpacing: '.22em', color: L.goldDeep, marginTop: 4 })}>{isZh ? '本月进程' : 'This month'}</div>
            <div style={{ fontSize: 11.5, lineHeight: 1.7, color: L.ink2 }}>
              {timeline.map((x, i) => <div key={i}>{fmtMD(x.date)} {x.text}</div>)}
            </div>
          </>
        )}
        {r.learned && (
          <>
            <div style={kick({ fontSize: 10, letterSpacing: '.22em', color: L.goldDeep, marginTop: 4 })}>{isZh ? '我们对您的新了解' : 'What we learned'}</div>
            <div style={{ fontSize: 11.5, lineHeight: 1.7, color: L.ink2, whiteSpace: 'pre-wrap' }}>{r.learned}</div>
          </>
        )}
        {plan.length > 0 && (
          <>
            <div style={kick({ fontSize: 10, letterSpacing: '.22em', color: L.goldDeep, marginTop: 4 })}>
              {nextMi != null ? (isZh ? `${ZH_MONTHS[nextMi]}计划` : `${EN_MONTHS[nextMi]} plan`) : (isZh ? '下月计划' : 'Next month')}
            </div>
            <ul style={{ margin: 0, paddingLeft: 16, fontSize: 11.5, lineHeight: 1.7, color: L.ink2 }}>
              {plan.map((x, i) => <li key={i}>{x}</li>)}
            </ul>
          </>
        )}
      </div>
    );
  }

  return (
    <div style={{ color: L.ink, fontFamily: L.sans }}>
      <header style={{ padding: '28px 0 22px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        <span style={kick()}>{kickerLine}</span>
        {mi != null && (
          <h1 style={{ margin: 0, fontFamily: L.serif, fontWeight: 400, fontSize: 54, lineHeight: 0.95, letterSpacing: '-.02em' }}>
            {isZh ? ZH_MONTHS[mi] : EN_MONTHS[mi]}
            {isZh && <><br /><span style={{ fontStyle: 'italic', color: L.goldDeep, fontSize: 40 }}>{EN_MONTHS[mi]}</span></>}
          </h1>
        )}
        {r.opening && (
          <p style={{ margin: '6px 0 0', fontFamily: "'Noto Serif SC', serif", fontSize: 15.5, lineHeight: 1.7, color: L.ink2, whiteSpace: 'pre-wrap' }}>{r.opening}</p>
        )}
      </header>

      <section aria-label={isZh ? '本月数字' : 'This month in numbers'}
        style={{ borderTop: `1px solid ${L.ink}`, borderBottom: rule, display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
        {STAT_KEYS.map(([k, , long, , en], i) => (
          <div key={k} style={{
            padding: i % 2 ? '18px 0 18px 16px' : '18px 0',
            borderBottom: i < 2 ? rule : 'none',
            borderRight: i % 2 ? 'none' : rule,
          }}>
            <div style={{ fontFamily: L.serif, fontSize: 44, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>{statVal(stats[k])}</div>
            <div style={{ fontSize: 12.5, color: L.ink2, marginTop: 6 }}>{isZh ? long : en}</div>
          </div>
        ))}
      </section>

      {timeline.length > 0 && (
        <section aria-label={isZh ? '本月进程' : 'This month'} style={{ paddingTop: 30, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <span style={kick({ color: L.goldDeep })}>{isZh ? '本月进程' : 'This month'}</span>
          <ol style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {timeline.map((x, i) => (
              <li key={i} style={{ display: 'grid', gridTemplateColumns: '58px minmax(0, 1fr)', gap: 12, padding: '12px 0', borderTop: rule }}>
                <span style={{ fontSize: 12, color: L.mute, fontVariantNumeric: 'tabular-nums' }}>{fmtMD(x.date)}</span>
                <span style={{ fontSize: 14, lineHeight: 1.6, overflowWrap: 'anywhere' }}>{x.text}</span>
              </li>
            ))}
          </ol>
        </section>
      )}

      {r.learned && (
        <section aria-label={isZh ? '我们对您的新了解' : 'What we learned'} style={{ paddingTop: 30, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <span style={kick({ color: L.goldDeep })}>{isZh ? '我们对您的新了解' : 'What we learned about you'}</span>
          <p style={{ margin: 0, fontFamily: "'Noto Serif SC', serif", fontSize: 15, lineHeight: 1.8, color: L.ink2, whiteSpace: 'pre-wrap' }}>{r.learned}</p>
        </section>
      )}

      {plan.length > 0 && (
        <section aria-label={isZh ? '下月计划' : 'Next month'} style={{ paddingTop: 30, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <span style={kick({ color: L.goldDeep })}>
            {nextMi != null ? (isZh ? `${ZH_MONTHS[nextMi]}计划` : `Plan for ${EN_MONTHS[nextMi]}`) : (isZh ? '下月计划' : 'Next month')}
          </span>
          <ul style={{ margin: 0, padding: '0 0 0 18px', display: 'flex', flexDirection: 'column', gap: 8, fontSize: 14, lineHeight: 1.6 }}>
            {plan.map((x, i) => <li key={i}>{x}</li>)}
          </ul>
        </section>
      )}

      {(authorName || reviewerName) && (
        <section aria-label={isZh ? '您的服务团队' : 'Your service team'}
          style={{ marginTop: 30, padding: 18, background: L.card, border: rule, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <span style={kick()}>{isZh ? '您的服务团队' : 'Your service team'}</span>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <div aria-hidden="true" style={{ width: 40, height: 40, borderRadius: '50%', background: L.paper2, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: L.serif, fontSize: 18, flexShrink: 0 }}>
              {(authorName[0] || '·').toUpperCase()}
            </div>
            <div style={{ minWidth: 0 }}>
              {authorName && <div style={{ fontSize: 14, fontWeight: 500 }}>{authorName} · {roleLabel}</div>}
              <div style={{ fontSize: 12.5, color: L.mute }}>
                {isZh
                  ? [authorName ? `本报告由 ${authorName} 撰写` : '', reviewerName ? `主管 ${reviewerName} 审核` : ''].filter(Boolean).join('，')
                  : [authorName ? `Written by ${authorName}` : '', reviewerName ? `reviewed by ${reviewerName}` : ''].filter(Boolean).join(', ')}
                {published ? ` · ${fmtMD(published)}` : ''}
              </div>
            </div>
          </div>
          {contactAction}
        </section>
      )}

      {footer}
    </div>
  );
}
